import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/layout/Navigation';
import SEO from '../components/SEO';
import Footer from '../components/layout/Footer';
import { useLanguage } from '../contexts/LanguageContext';
import QuickLinksBar from '../components/layout/QuickLinksBar';
import { Button } from '../components/ui/button';
import { ArrowRight, Award, MapPin, Check, Info } from 'lucide-react';
import { motion } from 'framer-motion';

const COGNIA_LOGO = 'https://customer-assets.emergentagent.com/job_global-continuity/artifacts/xiv5benz_61af194567ecd52c38758fd7_Cognia%20glossy%20logo-p-500.png';

export default function DiplomaPage() {
  const { t, language } = useLanguage();

  const pathways = [
    { icon: MapPin, title: t.diploma.pathways.france, desc: t.diploma.pathways.franceDesc, color: '#2D78F6' },
    { icon: MapPin, title: t.diploma.pathways.europe, desc: t.diploma.pathways.europeDesc, color: '#66CA9D' },
    { icon: MapPin, title: t.diploma.pathways.uk, desc: t.diploma.pathways.ukDesc, color: '#F8CD46' },
    { icon: MapPin, title: t.diploma.pathways.us, desc: t.diploma.pathways.usDesc, color: '#926EF7' },
  ];

  return (
    <div data-testid="diploma-page" className="min-h-screen bg-white">
      <SEO path="/diploma" />
      <Navigation />
      
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#14274B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight tracking-tight mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.diploma.heroTitle}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-lg text-white/70 max-w-2xl"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            {t.diploma.heroSubtitle}
          </motion.p>
        </div>
      </section>

      <QuickLinksBar />

      {/* Intro SEO */}
      <section className="py-12 bg-[#E7EAEF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#14274B] leading-relaxed mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'Le U.S. High School Diploma délivré par OFA France est un diplôme de fin d\'études secondaires américain, accrédité par COGNIA — le même organisme qui accrédite plus de 40 000 établissements scolaires dans plus de 100 pays. Ce diplôme est reconnu par les universités américaines, canadiennes, britanniques et internationales au même titre que celui délivré par n\'importe quelle high school américaine.'
              : 'The U.S. High School Diploma awarded by OFA France is an American secondary education diploma, accredited by COGNIA — the same organization that accredits over 40,000 schools in more than 100 countries. This diploma is recognized by American, Canadian, British and international universities just like one from any American high school.'}
          </p>
          <p className="text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {language === 'fr'
              ? 'Pour obtenir ce diplôme, les élèves cumulent des crédits académiques tout au long de leur scolarité chez OFA France, de la Grande Section à la Terminale. Le système de crédits américain est progressif : chaque année validée rapporte des crédits dans les matières fondamentales (ELA, Social Studies, Sciences, Mathématiques). En Terminale, l\'élève peut obtenir simultanément le Baccalauréat français et le U.S. High School Diploma.'
              : 'To obtain this diploma, students accumulate academic credits throughout their education at OFA France, from Kindergarten to 12th grade. The American credit system is progressive: each validated year earns credits in core subjects (ELA, Social Studies, Sciences, Mathematics). In their final year, students can simultaneously earn the French Baccalauréat and the U.S. High School Diploma.'}
          </p>
        </div>
      </section>

      {/* American Credit System Explanation */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2
                className="text-3xl md:text-4xl font-light text-[#14274B] mb-6"
                style={{ fontFamily: 'Gabarito, sans-serif' }}
              >
                {language === 'fr' ? 'Le système américain de crédits' : 'The American credit system'}
              </h2>
              <div className="space-y-4 text-[#475569] leading-relaxed" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <p>
                  {language === 'fr'
                    ? 'Aux États-Unis, le parcours scolaire du lycée (High School) est mesuré en crédits. Chaque matière étudiée pendant une année scolaire complète donne droit à 1 crédit.'
                    : 'In the United States, the high school pathway is measured in credits. Each subject studied for a full school year earns 1 credit.'}
                </p>
                <div className="bg-[#2D78F6]/5 border-l-4 border-[#2D78F6] p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Info className="w-5 h-5 text-[#2D78F6]" />
                    <span className="font-semibold text-[#14274B]">
                      {language === 'fr' ? '1 crédit = 1 année d\'étude d\'une matière' : '1 credit = 1 year of study of a subject'}
                    </span>
                  </div>
                  <p className="text-sm text-[#475569]">
                    {language === 'fr'
                      ? 'Par exemple, 4 crédits d\'English Language Arts signifient 4 années d\'étude de cette matière.'
                      : 'For example, 4 credits in English Language Arts means 4 years of study in that subject.'}
                  </p>
                </div>
                <p>
                  {language === 'fr'
                    ? 'Les élèves poursuivent leur scolarité dans le système éducatif français tout en accumulant les crédits requis pour l\'obtention du diplôme de fin d\'études secondaires américain : le High School Diploma.'
                    : 'Students continue their education in the French school system while accumulating the credits required to earn the American secondary school diploma: the High School Diploma.'}
                </p>
                <p className="font-medium text-[#14274B]">
                  {language === 'fr'
                    ? 'Pour obtenir ce diplôme américain, un élève doit accumuler un minimum de 24 crédits. (voir ci-dessous)'
                    : 'To earn this American diploma, a student must accumulate a minimum of 24 credits. (see below)'}
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-[#14274B] p-8 text-center"
            >
              <p className="text-6xl font-bold text-white mb-2" style={{ fontFamily: 'Gabarito, sans-serif' }}>24</p>
              <p className="text-white/80 text-lg mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {language === 'fr' ? 'crédits requis' : 'credits required'}
              </p>
              <div className="flex justify-center gap-4">
                <div className="bg-[#66CA9D] px-6 py-4 flex-1">
                  <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Gabarito, sans-serif' }}>17</p>
                  <p className="text-white/90 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {language === 'fr' ? 'Lycée Français' : 'French Lycée'}
                  </p>
                </div>
                <div className="bg-[#2D78F6] px-6 py-4 flex-1">
                  <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Gabarito, sans-serif' }}>7</p>
                  <p className="text-white/90 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    OFA France
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Credits Table */}
      <section className="py-16 bg-[#E7EAEF]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl md:text-4xl font-light text-[#14274B] mb-8"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Répartition des 24 crédits' : 'Breakdown of 24 credits'}
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white overflow-hidden"
          >
            {/* OFA France Header */}
            <div className="bg-[#2D78F6] px-6 py-3 flex items-center justify-between">
              <span className="text-white font-semibold" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                OFA France
              </span>
              <span className="text-white font-bold" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                7 {language === 'fr' ? 'crédits' : 'credits'}
              </span>
            </div>
            <table className="w-full" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <tbody>
                <tr className="border-b border-[#E7EAEF]">
                  <td className="px-6 py-3 text-[#14274B] font-medium">English Language Arts</td>
                  <td className="px-6 py-3 text-[#475569] text-sm">{language === 'fr' ? 'Littérature et composition anglaise' : 'English literature and composition'}</td>
                  <td className="px-6 py-3 text-right">
                    <span className="bg-[#2D78F6] text-white px-3 py-1 text-sm font-bold">4</span>
                  </td>
                </tr>
                <tr className="border-b border-[#E7EAEF]">
                  <td className="px-6 py-3 text-[#14274B] font-medium">US History</td>
                  <td className="px-6 py-3 text-[#475569] text-sm">{language === 'fr' ? 'Histoire des États-Unis' : 'United States History'}</td>
                  <td className="px-6 py-3 text-right">
                    <span className="bg-[#2D78F6] text-white px-3 py-1 text-sm font-bold">2</span>
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-3 text-[#14274B] font-medium">Introduction to AI</td>
                  <td className="px-6 py-3 text-[#475569] text-sm">{language === 'fr' ? 'Introduction à l\'Intelligence Artificielle' : 'Introduction to Artificial Intelligence'}</td>
                  <td className="px-6 py-3 text-right">
                    <span className="bg-[#2D78F6] text-white px-3 py-1 text-sm font-bold">1</span>
                  </td>
                </tr>
              </tbody>
            </table>

            {/* French Bac Header */}
            <div className="bg-[#66CA9D] px-6 py-3 flex items-center justify-between mt-1">
              <span className="text-white font-semibold" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                {language === 'fr' ? 'Lycée Français (Baccalauréat)' : 'French Lycée (Baccalaureate)'}
              </span>
              <span className="text-white font-bold" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                17 {language === 'fr' ? 'crédits' : 'credits'}
              </span>
            </div>
            <table className="w-full" style={{ fontFamily: 'Poppins, sans-serif' }}>
              <tbody>
                {(language === 'fr' ? [
                  { name: 'Littérature Française', desc: 'Français et littérature', c: 3 },
                  { name: 'Mathématiques', desc: 'Mathématiques niveau lycée', c: 3 },
                  { name: 'Sciences', desc: 'Physique, Chimie, SVT', c: 3 },
                  { name: 'Humanités', desc: 'Histoire-Géographie, SES', c: 3 },
                  { name: 'Langues Vivantes', desc: 'Anglais, espagnol, etc.', c: 2 },
                  { name: 'Philosophie', desc: 'Philosophie terminale', c: 1 },
                  { name: 'Éducation Physique', desc: 'EPS', c: 1 },
                  { name: 'Arts', desc: 'Arts plastiques ou musique', c: 1 },
                ] : [
                  { name: 'French Literature', desc: 'French language and literature', c: 3 },
                  { name: 'Mathematics', desc: 'High school mathematics', c: 3 },
                  { name: 'Science', desc: 'Physics, Chemistry, Biology', c: 3 },
                  { name: 'Humanities', desc: 'History-Geography, Social Sciences', c: 3 },
                  { name: 'Foreign Languages', desc: 'English, Spanish, etc.', c: 2 },
                  { name: 'Philosophy', desc: 'Terminal year philosophy', c: 1 },
                  { name: 'Physical Education', desc: 'PE', c: 1 },
                  { name: 'Arts', desc: 'Visual arts or music', c: 1 },
                ]).map((item, i, arr) => (
                  <tr key={i} className={i < arr.length - 1 ? 'border-b border-[#E7EAEF]' : ''}>
                    <td className="px-6 py-3 text-[#14274B] font-medium">{item.name}</td>
                    <td className="px-6 py-3 text-[#475569] text-sm">{item.desc}</td>
                    <td className="px-6 py-3 text-right">
                      <span className="bg-[#66CA9D] text-white px-3 py-1 text-sm font-bold">{item.c}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Total */}
            <div className="bg-[#14274B] px-6 py-4 flex items-center justify-between">
              <span className="text-white font-bold text-lg" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                TOTAL
              </span>
              <span className="text-white font-bold text-2xl" style={{ fontFamily: 'Gabarito, sans-serif' }}>
                24 {language === 'fr' ? 'crédits' : 'credits'}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* COGNIA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-[#F8CD46]/20 flex items-center justify-center">
                  <Award className="w-7 h-7 text-[#F8CD46]" />
                </div>
              </div>
              <h2
                className="text-3xl md:text-4xl font-light text-[#14274B] mb-4"
                style={{ fontFamily: 'Gabarito, sans-serif' }}
              >
                {language === 'fr' ? 'OFA Lycée est accrédité par COGNIA' : 'OFA Lycée is accredited by COGNIA'}
              </h2>
              <p
                className="text-lg text-[#14274B] font-medium mb-4"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {language === 'fr' ? 'Seule école française en ligne accréditée aux États-Unis.' : 'The only French online school accredited in the United States.'}
              </p>
              <p
                className="text-base text-[#475569] leading-relaxed mb-4"
                style={{ fontFamily: 'Poppins, sans-serif' }}
              >
                {language === 'fr'
                  ? 'L\'accréditation Cognia représente les politiques et procédures unifiées d\'accréditation et de certification de ses divisions :'
                  : 'Cognia accreditation represents the unified policies and procedures for accreditation and certification from its Accreditation Divisions:'}
              </p>
              <ul className="text-sm text-[#475569] space-y-1 mb-8 list-disc list-inside" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <li>North Central Association Commission on Accreditation and School Improvement (NCA CASI)</li>
                <li>Northwest Accreditation Commission (NWAC)</li>
                <li>Southern Association of Colleges and Schools Council on Accreditation and School Improvement (SACS CASI)</li>
              </ul>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-3xl font-bold text-[#2D78F6]" style={{ fontFamily: 'Gabarito, sans-serif' }}>40 000</p>
                  <p className="text-xs uppercase tracking-wider text-[#475569] mt-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {language === 'fr' ? 'Établissements' : 'Institutions'}
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#2D78F6]" style={{ fontFamily: 'Gabarito, sans-serif' }}>100+</p>
                  <p className="text-xs uppercase tracking-wider text-[#475569] mt-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {language === 'fr' ? 'Pays' : 'Countries'}
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#2D78F6]" style={{ fontFamily: 'Gabarito, sans-serif' }}>17M</p>
                  <p className="text-xs uppercase tracking-wider text-[#475569] mt-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {language === 'fr' ? 'Élèves' : 'Students'}
                  </p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="flex items-center justify-center"
            >
              <img src={COGNIA_LOGO} alt="Cognia Accredited" className="w-64 h-64 object-contain" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pathways */}
      <section className="py-20 bg-[#E7EAEF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-3xl md:text-4xl font-light text-[#14274B] mb-12"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.diploma.pathways.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pathways.map((pathway, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                data-testid={`pathway-${index}`}
                className="bg-white p-8"
              >
                <div
                  className="w-12 h-12 flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${pathway.color}20` }}
                >
                  <pathway.icon className="w-6 h-6" style={{ color: pathway.color }} />
                </div>
                <h3 className="text-xl font-semibold text-[#14274B] mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {pathway.title}
                </h3>
                <p className="text-[#475569]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {pathway.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#2D78F6]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl font-light text-white mb-6"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {t.cta.title}
          </h2>
          <a href="https://admissions.ofalycee.fr" target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="diploma-cta"
              size="lg"
              className="bg-white text-[#14274B] hover:bg-[#14274B] hover:text-white px-8 py-6"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {t.cta.primary}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
